import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ACTIONS_COL_WIDTH,
  Badge,
  DataTable,
  PageHeader,
  RowActions,
  type DataTableColumn,
} from '@/components/ui';
import { usePermissions } from '../../../hooks/usePermissions';
import NewHiredEquipmentModal from '../../hired-equipment/components/NewHiredEquipmentModal';
import { persistHiredEquipmentAnchor } from '../../hired-equipment/utils/anchorStorage';
import { formatAnchorValue, methodLabel } from '../../hired-equipment/utils/buildAnchor';
import { formatCurrency } from '../../hired-equipment/utils/gridMath';
import { readSessionSubmissions } from '../../hired-equipment/utils/sessionSubmissions';
import type {
  HiredEquipmentAnchor,
  HiredEquipmentSessionStatus,
  HiredEquipmentSessionSubmission,
} from '../../hired-equipment/types';

const STATUS_BADGE: Record<
  HiredEquipmentSessionStatus,
  { bg: 'warning' | 'success' | 'danger'; text?: 'dark'; label: string }
> = {
  pending: { bg: 'warning', text: 'dark', label: 'Pending' },
  approved: { bg: 'success', label: 'Approved' },
  need_revisions: { bg: 'danger', label: 'Need Revisions' },
  declined: { bg: 'danger', label: 'Declined' },
};

function formatCreated(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(d);
}

const FormsPage: React.FC = () => {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [showHeModal, setShowHeModal] = useState(false);
  const canCreateHiredEquipment = hasPermission('submission:he_manage');
  const submissions = useMemo(() => readSessionSubmissions(), [showHeModal]);

  const handleContinue = (anchor: HiredEquipmentAnchor) => {
    persistHiredEquipmentAnchor(anchor);
    setShowHeModal(false);
    navigate('/hired-equipment/grid', { state: { anchor } });
  };

  const goToApprovals = () => navigate('/approvals');

  const columns: DataTableColumn<HiredEquipmentSessionSubmission>[] = [
    {
      key: 'ticket',
      header: 'Ticket number',
      mobile: 'title',
      cell: (row) => (
        <span className="tw-font-semibold tw-tabular-nums tw-text-content">{row.id}</span>
      ),
    },
    {
      key: 'created',
      header: 'Created',
      mobileLabel: 'Created',
      cell: (row) => formatCreated(row.createdAt),
    },
    {
      key: 'method',
      header: 'Method',
      mobileLabel: 'Method',
      cell: (row) => methodLabel(row.anchor.entryMethod),
    },
    {
      key: 'anchor',
      header: 'Anchor',
      mobile: 'subtitle',
      cell: (row) => formatAnchorValue(row.anchor),
    },
    {
      key: 'lines',
      header: 'Lines',
      align: 'right',
      mobileLabel: 'Lines',
      cell: (row) => <span className="tw-tabular-nums">{row.lineCount}</span>,
    },
    {
      key: 'value',
      header: 'Value',
      align: 'right',
      mobileLabel: 'Value',
      cell: (row) => (
        <span className="tw-font-semibold tw-tabular-nums">{formatCurrency(row.totalValue)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      mobile: 'trailing',
      cell: (row) => {
        const status = STATUS_BADGE[row.status] ?? STATUS_BADGE.pending;
        return (
          <Badge bg={status.bg} text={status.text} pill>
            {status.label}
          </Badge>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      width: ACTIONS_COL_WIDTH,
      mobile: 'hidden',
      cell: (row) => (
        <RowActions
          maxInline={1}
          actions={[
            {
              kind: 'view',
              label: 'View in Submissions',
              onClick: () =>
                navigate(
                  row.formId
                    ? `/approvals?formId=${encodeURIComponent(row.formId)}`
                    : '/approvals',
                ),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-y-auto tw-p-3 md:tw-p-4">
      <PageHeader
        title="Forms"
        subtitle="Enter hired equipment and subcontractor work. Submitted records go to Submissions."
        actions={[
          {
            key: 'approvals',
            label: 'Submissions',
            icon: 'bi-clipboard-check',
            intent: 'secondary',
            alwaysShowLabel: true,
            onClick: goToApprovals,
          },
          {
            key: 'new-he',
            label: 'New Hired Equipment',
            icon: 'bi-plus-lg',
            intent: 'primary',
            alwaysShowLabel: true,
            hidden: !canCreateHiredEquipment,
            onClick: () => setShowHeModal(true),
          },
        ]}
      />

      <DataTable
        columns={columns}
        data={submissions}
        rowKey={(row) => row.id}
        size="sm"
        striped
        bordered
        hover
        framed
        emptyMessage="No submissions this session. Use New Hired Equipment to start one."
      />

      <NewHiredEquipmentModal
        show={showHeModal}
        onHide={() => setShowHeModal(false)}
        onContinue={handleContinue}
      />
    </div>
  );
};

export default FormsPage;
